"use client"

/**
 * Isolated Luigi's Box collector dispatch.
 *
 * Luigi's collector (`cdn.luigisbox.tech/<lbx>.js`) intercepts
 * `window.dataLayer.push` and matches on either:
 *   - standard GA4 `{ event: "select_item", ecommerce: { items: [...] } }`
 *   - lbx-prefixed `{ event: "lbx_select_item", ecommerce: { items: [...] } }`
 *
 * After PR #3542 (Apr 15, 2026) the rest of the app stopped emitting the
 * GA4 `ecommerce` envelope. All pushes now use `eventModel`, so Luigi
 * sees no items and drops every click. Reverting `eventModel` was rejected
 * because the GTM/GA4 pipeline relies on it and was painful to debug.
 *
 * This module emits a SEPARATE `lbx_*` push that only Luigi consumes, so
 * the existing eventModel contract stays untouched. See DEV-9479.
 */

export interface LuigiItem {
  item_id: string
  item_name?: string
  price?: number
  index?: number
  type?: "item" | "category"
}

interface LuigiSelectItemPayload {
  items: LuigiItem[]
  item_list_name?: string
  search_term?: string
}

/**
 * Luigi attributes list performance per recommender, so the widget identity
 * has to travel with the impression. Luigi reads it from `filters` on the
 * collector payload.
 */
export interface LuigiListFilters {
  RecommenderClientId?: string
  RecommendationId?: string
  Recommender?: string
  Type?: string
  ItemIds?: string[]
  SearchTerm?: string
}

interface LuigiViewItemListPayload {
  items: LuigiItem[]
  item_list_name?: string
  search_term?: string
  filters?: LuigiListFilters
}

interface LuigiViewItemPayload {
  items: LuigiItem[]
}

interface LuigiAddToCartPayload {
  items: LuigiItem[]
  currency?: string
  value?: number
}

interface LuigiPurchasePayload {
  items: LuigiItem[]
  transaction_id: string
  value: number
  currency?: string
}

const pushLbx = (event: string, payload: unknown) => {
  try {
    if (typeof window === "undefined") return false
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event, ecommerce: payload })
    return true
  } catch {
    return false
  }
}

export const trackLuigiSelectItem = (payload: LuigiSelectItemPayload) =>
  pushLbx("lbx_select_item", payload)

export const trackLuigiViewItemList = (payload: LuigiViewItemListPayload) =>
  pushLbx("lbx_view_item_list", payload)

export const trackLuigiViewItem = (payload: LuigiViewItemPayload) =>
  pushLbx("lbx_view_item", payload)

export const trackLuigiAddToCart = (payload: LuigiAddToCartPayload) =>
  pushLbx("lbx_add_to_cart", payload)

export const trackLuigiPurchase = (payload: LuigiPurchasePayload) =>
  pushLbx("lbx_purchase", payload)

// `purchase` is emitted directly via trackLuigiPurchase from purchase.ts
// (the GA4 `purchase` dispatch carries `eventModel`, not `ecommerce`, so it
// would not mirror anyway, but listing it would imply a path that doesn't
// exist).
const GA4_TO_LBX: Record<string, string> = {
  add_to_cart: "lbx_add_to_cart",
  view_item: "lbx_view_item",
}

/**
 * Side-emit an `lbx_*` mirror for Luigi. Called from `dispatchDataLayerPush`
 * BEFORE normalization strips `ecommerce` from the payload.
 *
 * `select_item` and `view_item_list` are handled at call sites
 * (searchEvents.ts, useAutocomplete) because they no longer carry `ecommerce`
 * after PR #3542.
 */
export const mirrorToLuigi = (payload: Record<string, unknown>) => {
  try {
    const event = payload.event
    if (typeof event !== "string") return
    const lbxEvent = GA4_TO_LBX[event]
    if (!lbxEvent) return
    const ecommerce = payload.ecommerce as Record<string, unknown> | undefined
    if (!ecommerce) return
    const items = ecommerce.items as Array<Record<string, unknown>> | undefined
    if (!items?.length || !items.every((i) => i?.item_id)) return
    pushLbx(lbxEvent, ecommerce)
  } catch {
    // never throw from a mirror
  }
}
